// src/components/ui/LayoutShell.jsx
import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { writeLastAuthedPath } from "@/lib/authedPathCache";
import MobileTabBar from "@/components/nav/MobileTabBar.jsx";

function MobileTabSpacer() {
  return <div className="h-[calc(96px+env(safe-area-inset-bottom))]" aria-hidden="true" />;
}

function isBlockedPath(pathname = "") {
  return (
    pathname === "/" ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/auth/") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password")
  );
}

export default function LayoutShell({
  children,
  sidebar = null,

  // legacy / compatibility
  hideHeader = true,

  // tabs
  hideTabs = false,

  // ✅ optional: allow some screens (profiles) to go flush-top on mobile
  flushTop = false,

  // ✅ allow overriding rail modifier if ever needed
  railClassName = "afrimed-rail afrimed-rail--comfortable",
  shellClassName = "",
  manageOwnTabLayout = false,
  freeScroll = false,
}) {
  const hasSidebar = !!sidebar;
  const showTabs = !hideTabs;
  const mainRef = React.useRef(null);

  const auth = useAuth() || {};
  const { user, loading } = auth;
  const userId = user?.id || null;

  const location = useLocation();

  // ✅ Track last "safe" authed route for post-login resume
  React.useEffect(() => {
    if (!userId) return;

    const pathname = location?.pathname || "";
    if (isBlockedPath(pathname)) return;

    const fullPath =
      (location?.pathname || "/") +
      (location?.search || "") +
      (location?.hash || "");

    try {
      writeLastAuthedPath(fullPath, userId);
    } catch {}
  }, [userId, location?.pathname, location?.search, location?.hash]);

  React.useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0, left: 0 });
  }, [location.pathname]);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const html = document.documentElement;
    const body = document.body;
    if (!showTabs || freeScroll) return undefined;

    html.classList.add("afrimed-auth-mobile-shell");
    body?.classList.add("afrimed-auth-mobile-shell");

    return () => {
      html.classList.remove("afrimed-auth-mobile-shell");
      body?.classList.remove("afrimed-auth-mobile-shell");
    };
  }, [freeScroll, showTabs]);

  return (
    <div
      className={[
        freeScroll
          ? "flex min-h-[100svh] flex-col overflow-visible bg-[rgb(var(--clr-surface-container-low))] text-[rgb(var(--clr-on-surface))]"
          : "fixed inset-0 flex h-[100dvh] flex-col overflow-hidden bg-[rgb(var(--clr-surface-container-low))] text-[rgb(var(--clr-on-surface))] md:static md:inset-auto md:h-auto md:min-h-[100dvh] md:overflow-visible",
        shellClassName,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        className={
          hasSidebar
            ? "w-full flex-1 grid lg:grid-cols-[260px_minmax(0,1fr)] gap-4"
            : "w-full flex-1"
        }
      >
        {hasSidebar && (
          <aside className="hidden lg:block sticky top-[calc(56px+16px)] self-start">
            {sidebar}
          </aside>
        )}

        <main
          ref={mainRef}
          className={
            freeScroll
              ? "min-h-0 w-full overflow-visible"
              : "min-h-0 w-full flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] md:overflow-visible"
          }
        >
          <div
            className={
              manageOwnTabLayout
                ? railClassName
                : [
                    railClassName,
                    flushTop ? "pt-0" : "pt-4",
                    "pb-6",
                  ].join(" ")
            }
          >
            {children}
            {showTabs && !manageOwnTabLayout ? <MobileTabSpacer /> : null}
          </div>
        </main>
      </div>

      {showTabs ? <MobileTabBar /> : null}
    </div>
  );
}
